// Read-only OEM administration API (documentation/oem-partner-architecture.md section C). The OEM
// area uses ONLY these six /api/oem/* routes - never the generic customer, network or radio
// routes. No OEM or customer id is ever sent as a claim or header: the server derives the OEM
// from the signed-in user's current database state.

export interface OEMSelf {
  id: string;
  name: string;
  legal_name: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  support_email: string;
  support_phone: string;
  support_url: string;
}

export interface OEMCustomerView {
  id: string;
  name: string;
  created_at: string;
}

export interface OEMNetworkView {
  id: string;
  name: string;
  created_at: string;
}

export interface OEMDepartmentView {
  id: string;
  name: string;
  description: string;
  parent_department_id: string | null;
}

export interface OEMUserRoleView {
  role: string;
  department_id: string | null;
  department_name: string | null;
}

export interface OEMUserView {
  id: string;
  username: string;
  email: string;
  disabled: boolean;
  network_id: string;
  legacy_role: string;
  roles: OEMUserRoleView[];
}

/** What went wrong, in user terms (no backend internals). */
export type OEMErrorKind = 'unauthenticated' | 'forbidden' | 'not-found' | 'unavailable' | 'malformed';

export class OEMApiError extends Error {
  constructor(public kind: OEMErrorKind, public status = 0) {
    super(oemErrorMessage(kind));
  }
}

export function oemErrorMessage(kind: OEMErrorKind): string {
  switch (kind) {
    case 'unauthenticated':
      return 'Your session has ended. Please sign in again.';
    case 'forbidden':
      return 'Your account does not currently have OEM administration access.';
    case 'not-found':
      return 'This customer is not available to your OEM. It may have been moved to another OEM Partner.';
    case 'malformed':
      return 'The server returned an unexpected response. Please try again later.';
    default:
      return 'RadioOS is not reachable right now. Please try again later.';
  }
}

export function errorKind(status: number): OEMErrorKind {
  if (status === 401) return 'unauthenticated';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not-found';
  return 'unavailable';
}

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const hasId = (v: unknown) => isObj(v) && typeof v.id === 'string' && v.id !== '';

async function get<T>(path: string, valid: (v: unknown) => boolean): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, { headers: { Accept: 'application/json' } });
  } catch {
    throw new OEMApiError('unavailable');
  }
  if (!response.ok) throw new OEMApiError(errorKind(response.status), response.status);
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw new OEMApiError('malformed', response.status);
  }
  if (!valid(body)) throw new OEMApiError('malformed', response.status);
  return body as T;
}

const listOf = (v: unknown) => Array.isArray(v) && v.every(hasId);
const seg = encodeURIComponent;

export const OEM_API = {
  partner: '/api/oem/partner',
  customers: '/api/oem/customers',
  customer: (id: string) => `/api/oem/customers/${seg(id)}`,
  networks: (id: string) => `/api/oem/customers/${seg(id)}/networks`,
  departments: (id: string) => `/api/oem/customers/${seg(id)}/departments`,
  users: (id: string) => `/api/oem/customers/${seg(id)}/users`,
};

export const getOEMSelf = () => get<OEMSelf>(OEM_API.partner, hasId);
export const getOEMCustomers = () => get<OEMCustomerView[]>(OEM_API.customers, listOf);
export const getOEMCustomer = (id: string) => get<OEMCustomerView>(OEM_API.customer(id), hasId);
export const getOEMNetworks = (id: string) => get<OEMNetworkView[]>(OEM_API.networks(id), listOf);
export const getOEMDepartments = (id: string) => get<OEMDepartmentView[]>(OEM_API.departments(id), listOf);
export const getOEMUsers = (id: string) =>
  get<OEMUserView[]>(OEM_API.users(id), (v) => listOf(v) && (v as unknown[]).every((u) => Array.isArray((u as { roles?: unknown }).roles)));
